import React, { useEffect, useMemo, useState } from 'react';
import {
  MapContainer,
  TileLayer,
  Polyline,
  Marker,
  Tooltip,
  useMap,
} from 'react-leaflet';
import { LatLngBoundsExpression, LatLngTuple, divIcon } from 'leaflet';
import {
  Paper,
  Grid,
  Typography,
  IconButton,
  Collapse,
  Checkbox,
  CircularProgress,
  Box,
} from '@material-ui/core';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';

import api from '../../../../../services/api';
import { useAuth } from '../../../../../hooks/auth';
import { PERMISSIONS } from '../../../../../constants';

import { useStyles } from './styles';

interface IMapStop {
  ordem: number;
  orderCode: string;
  orderNumber: string;
  client: string;
  status: string;
  lat: number | null;
  long: number | null;
}

interface IMapRoute {
  id: number;
  name: string;
  status: string;
  driverName: string;
  color: string;
  ungeocoded: number;
  stops: IMapStop[];
}

const FitBounds = ({ points }: { points: LatLngTuple[] }) => {
  const map = useMap();

  useEffect(() => {
    if (points.length > 0) {
      map.fitBounds(points as LatLngBoundsExpression, { padding: [30, 30] });
    }
  }, [map, points]);

  return null;
};

export const DayRoutesMap = () => {
  const {
    data: { user, permissions },
  } = useAuth();
  const classes = useStyles();

  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(true);
  const [routes, setRoutes] = useState<IMapRoute[]>([]);
  const [hidden, setHidden] = useState<Record<number, boolean>>({});
  // Traçado real pelas vias (OSRM), por rota. Enquanto não chega (ou se o
  // serviço falhar), a rota aparece como linha reta entre as paradas.
  const [roadPaths, setRoadPaths] = useState<Record<number, LatLngTuple[]>>(
    {}
  );

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      try {
        const response = await api.get(
          `/route/map-points?companyDocument=${user.companyDocument}`
        );

        if (mounted) setRoutes(response.data.routes || []);
      } catch {
        if (mounted) setRoutes([]);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    load();

    return () => {
      mounted = false;
    };
  }, [user.companyDocument]);

  useEffect(() => {
    if (routes.length === 0) return undefined;

    let mounted = true;
    const controller = new AbortController();

    // Uma consulta ao OSRM por rota (paradas na ordem como waypoints),
    // em sequência para não sobrecarregar o serviço público.
    const trace = async () => {
      for (const route of routes) {
        const stops = route.stops.filter(
          (stop) => stop.lat !== null && stop.long !== null
        );
        if (stops.length < 2) continue;

        const coordinates = stops
          .map((stop) => `${stop.long},${stop.lat}`)
          .join(';');

        try {
          const response = await fetch(
            `https://router.project-osrm.org/route/v1/driving/${coordinates}?overview=full&geometries=geojson`,
            { signal: controller.signal }
          );
          if (!response.ok) continue;

          const data = await response.json();
          const geometry: [number, number][] =
            data?.routes?.[0]?.geometry?.coordinates || [];
          if (!mounted || geometry.length === 0) continue;

          setRoadPaths((previous) => ({
            ...previous,
            [route.id]: geometry.map(
              ([long, lat]) => [lat, long] as LatLngTuple
            ),
          }));
        } catch {
          // Sem OSRM: a rota fica na linha reta (fallback visual).
        }

        if (!mounted) break;
      }
    };

    trace();

    return () => {
      mounted = false;
      controller.abort();
    };
  }, [routes]);

  const markerIcon = (color: string, index: number) =>
    divIcon({
      className: classes.customMarker,
      html: `
        <div style="background-color:${color};" class="marker-pin"></div>
        <b>${index}</b>
      `,
      iconSize: [26, 26],
      iconAnchor: [13, 13],
      tooltipAnchor: [0, -14],
    });

  const visibleRoutes = useMemo(
    () => routes.filter((route) => !hidden[route.id]),
    [routes, hidden]
  );

  const allPoints = useMemo(() => {
    const points: LatLngTuple[] = [];

    visibleRoutes.forEach((route) => {
      route.stops.forEach((stop) => {
        if (stop.lat !== null && stop.long !== null) {
          points.push([stop.lat, stop.long]);
        }
      });
    });

    return points;
  }, [visibleRoutes]);

  if (!permissions.includes(PERMISSIONS.ROUTE_VIEW_MAP)) return null;
  if (!loading && routes.length === 0) return null;

  return (
    <Paper>
      <div className={classes.header}>
        <div>
          <Typography variant="h6">Mapa das rotas do dia</Typography>
          <Typography variant="body2" color="textSecondary">
            {routes.length} rota{routes.length === 1 ? '' : 's'} · pontos na
            ordem de entrega · traçado pelas vias · cor por rota
          </Typography>
        </div>
        <IconButton
          onClick={() => setOpen(!open)}
          aria-label={open ? 'Recolher mapa' : 'Exibir mapa'}
        >
          {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </div>

      <Collapse in={open}>
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container>
            <Grid item xs={12} md={9}>
              <MapContainer
                center={allPoints[0] || [-22.5, -47.8]}
                zoom={10}
                scrollWheelZoom
                style={{ width: '100%', height: '480px' }}
              >
                <TileLayer
                  attribution='&copy; <a href="https://4innovation.co/">4INNOVATION TECNOLOGIA</a>'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <FitBounds points={allPoints} />

                {visibleRoutes.map((route) => {
                  const geocodedStops = route.stops.filter(
                    (stop) => stop.lat !== null && stop.long !== null
                  );
                  const line: LatLngTuple[] = geocodedStops.map((stop) => [
                    stop.lat as number,
                    stop.long as number,
                  ]);

                  const roadPath = roadPaths[route.id];

                  return (
                    <React.Fragment key={route.id}>
                      <Polyline
                        pathOptions={{
                          color: route.color,
                          weight: 4,
                          // Linha reta = ainda sem o traçado das vias;
                          // tracejada para o operador perceber a diferença.
                          dashArray: roadPath ? undefined : '8 8',
                          opacity: roadPath ? 0.9 : 0.6,
                        }}
                        positions={roadPath || line}
                      />
                      {geocodedStops.map((stop) => (
                        <Marker
                          key={`${route.id}-${stop.ordem}`}
                          position={[stop.lat as number, stop.long as number]}
                          icon={markerIcon(route.color, stop.ordem)}
                        >
                          <Tooltip direction="top" opacity={1}>
                            <p>
                              <strong>Pedido:</strong> {stop.orderNumber}
                            </p>
                            <p>
                              <strong>Cliente:</strong> {stop.client}
                            </p>
                            <p>
                              Parada {stop.ordem} de {route.stops.length} ·{' '}
                              {route.name} · {route.driverName}
                            </p>
                          </Tooltip>
                        </Marker>
                      ))}
                    </React.Fragment>
                  );
                })}
              </MapContainer>
            </Grid>

            <Grid item xs={12} md={3} className={classes.legend}>
              {routes.map((route) => (
                <div key={route.id}>
                  <label className={classes.legendItem}>
                    <Checkbox
                      size="small"
                      color="primary"
                      checked={!hidden[route.id]}
                      onChange={() =>
                        setHidden((previous) => ({
                          ...previous,
                          [route.id]: !previous[route.id],
                        }))
                      }
                    />
                    <span
                      className={classes.legendChip}
                      style={{ backgroundColor: route.color }}
                    >
                      {route.name}
                    </span>
                  </label>
                  <div className={classes.legendMeta}>
                    {route.driverName} · {route.stops.length} paradas
                    {route.ungeocoded > 0 &&
                      ` · ${route.ungeocoded} sem coordenada`}
                  </div>
                </div>
              ))}
            </Grid>
          </Grid>
        )}
      </Collapse>
    </Paper>
  );
};
